import CloseRounded from "@mui/icons-material/CloseRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import type { ReactNode } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { darken, lighten, rgbToHex, useColorScheme, useTheme } from "@mui/material/styles";
import { initials } from "../avatar";
import MyLocationRounded from "@mui/icons-material/MyLocationRounded";
import { useGeolocation } from "@bw/shared/hooks";
import {
  useFacilityMapController,
  type FacilityMapCenter,
  type FacilityMapControllerOptions,
  type FacilityMapFacility,
  type FacilityMapProps,
} from "@bw/shared/components";
import { useEffect, useMemo, useRef, useState } from "react";

export type MaterialFacilityMapProps =
  | (FacilityMapProps & {
      mode?: "discovery";
      hideUnavailable?: boolean;
      allowAreaSearch?: boolean;
      initialSelectedFacilityId?: string;
      /** Fill the available screen height (the Play map view). */
      fill?: boolean;
      /** Controls floated over the top of the map (count, List, Add place). */
      overlay?: ReactNode;
      /**
       * Offer the viewer's own position as a blue dot (adults only; the caller
       * decides). Read in the browser, never stored or sent.
       */
      showUserLocation?: boolean;
    })
  | (Omit<FacilityMapProps, "onSearchArea" | "onSelectFacility"> & {
      mode: "location";
      hideUnavailable?: boolean;
      allowAreaSearch?: boolean;
      initialSelectedFacilityId?: string;
      onSearchArea?: never;
      onSelectFacility?: never;
      fill?: never;
      overlay?: never;
      showUserLocation?: never;
    });

/**
 * The viewer's position for the map, acquired only through the shared
 * tap-to-locate hook (ADR-024: never on mount). A fix that lands after the
 * caller withdrew permission to show it is dropped, and so is its pan.
 */
function useViewerLocation(enabled: boolean, onFix: (point: FacilityMapCenter) => void) {
  const geo = useGeolocation();
  const supported = enabled && typeof navigator !== "undefined" && "geolocation" in navigator;
  const location =
    supported && geo.status === "granted" && geo.latitude !== undefined && geo.longitude !== undefined
      ? { latitude: geo.latitude, longitude: geo.longitude }
      : null;
  const panPending = useRef(false);
  const onFixRef = useRef(onFix);
  onFixRef.current = onFix;
  useEffect(() => {
    if (!supported) panPending.current = false;
    if (location && panPending.current) {
      panPending.current = false;
      onFixRef.current(location);
    }
  }, [supported, location?.latitude, location?.longitude]);
  const locate = () => {
    if (!supported) return;
    panPending.current = true;
    geo.request();
  };
  return { location, locate, supported, status: geo.status };
}

type MapStyles = NonNullable<FacilityMapControllerOptions["styles"]>;

/** Maps styles take #rrggbb; theme colours may be rgb()/rgba(). */
function toHex(color: string): string {
  return color.startsWith("#") ? color : rgbToHex(color);
}

/** A quiet dark basemap built from the theme, so pills and places stand out. */
function darkMapStyles(background: string, mutedText: string): MapStyles {
  const tone = (amount: number) => toHex(lighten(background, amount));
  return [
    { elementType: "geometry", stylers: [{ color: tone(0.06) }] },
    { elementType: "labels.text.fill", stylers: [{ color: toHex(mutedText) }] },
    { elementType: "labels.text.stroke", stylers: [{ color: toHex(background) }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: tone(0.14) }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: tone(0.2) }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: toHex(darken(background, 0.2)) }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ visibility: "on" }, { color: tone(0.1) }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
  ];
}

const ignoreSearchArea = () => {};

/**
 * Player-native MUI composition over the shared headless Google Maps
 * controller. Map lifecycle and coordinate behavior stay shared; toolbar,
 * loading/fallback states, and marker details follow the Player theme.
 */
export function MaterialFacilityMap({
  facilities,
  center,
  onSelectFacility,
  onSearchArea,
  zoom = 11,
  getGoogleMapsApiKey,
  loadGoogleMaps,
  mode = "discovery",
  hideUnavailable = false,
  allowAreaSearch = true,
  initialSelectedFacilityId,
  fill = false,
  overlay,
  showUserLocation = false,
}: MaterialFacilityMapProps) {
  const theme = useTheme();
  // The app switches light/dark through CSS colour schemes, so read the
  // active scheme's concrete palette rather than the default one.
  const { mode: colorMode, systemMode } = useColorScheme();
  const scheme = (colorMode === "system" ? systemMode : colorMode) ?? theme.defaultColorScheme ?? "light";
  const palette = theme.colorSchemes?.[scheme]?.palette ?? theme.palette;
  const dark = scheme === "dark";
  const discovery = mode === "discovery";
  // Read synchronously: map options are fixed when the map is created.
  const isPhone = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const viewer = useViewerLocation(showUserLocation, (point) => controller.panTo(point));
  const background = palette.background.default;
  const mutedText = palette.text.secondary;
  const styles = useMemo(
    () => (dark ? darkMapStyles(background, mutedText) : undefined),
    [dark, background, mutedText],
  );
  const { paper } = palette.background;
  const { divider } = palette;
  const textPrimary = palette.text.primary;
  const { main: primaryMain, contrastText } = palette.primary;
  // Stable across renders so markers rebuild only when the theme changes.
  const pillColors = useMemo(
    () =>
      discovery
        ? { fill: paper, stroke: divider, text: textPrimary, highlightFill: primaryMain, highlightText: contrastText }
        : undefined,
    [discovery, paper, divider, textPrimary, primaryMain, contrastText],
  );
  const locationDot = palette.info.main;
  const [selectedFacility, setSelectedFacility] =
    useState<FacilityMapFacility | null>(null);
  const restoredId = useRef<string | null>(null);
  const restoreFocus = useRef(false);
  const detailsRef = useRef<HTMLElement>(null);
  const controller = useFacilityMapController({
    facilities,
    center,
    onSearchArea: allowAreaSearch
      ? (onSearchArea ?? ignoreSearchArea)
      : ignoreSearchArea,
    zoom,
    getGoogleMapsApiKey,
    loadGoogleMaps,
    styles,
    pillColors,
    // The full-screen Play map behaves like Google Maps: one finger pans.
    interaction: fill ? "app" : "embedded",
    zoomButtons: !isPhone,
    userLocation: viewer.location,
    onMarkerActivate: ({ facility }) => setSelectedFacility(facility),
  });

  useEffect(() => {
    setSelectedFacility((current) =>
      current
        ? (facilities.find(
            (facility) => facility.facility_id === current.facility_id,
          ) ?? null)
        : null,
    );
  }, [facilities]);

  useEffect(() => {
    if (!initialSelectedFacilityId) {
      restoredId.current = null;
      return;
    }
    if (restoredId.current === initialSelectedFacilityId) return;
    const facility = facilities.find(
      (item) => item.facility_id === initialSelectedFacilityId,
    );
    if (!facility) return;
    restoredId.current = initialSelectedFacilityId;
    restoreFocus.current = true;
    setSelectedFacility(facility);
  }, [facilities, initialSelectedFacilityId]);

  useEffect(() => {
    if (restoreFocus.current && detailsRef.current) {
      detailsRef.current.focus({ preventScroll: true });
      detailsRef.current.scrollIntoView?.({ block: "nearest" });
      restoreFocus.current = false;
    }
  }, [selectedFacility]);

  if (controller.loadState === "unavailable") {
    if (hideUnavailable) return null;
    return (
      <Stack spacing={1}>
        {/* The floating controls (List, Add place) stay reachable without a map. */}
        {overlay}
        <Alert severity="info" role="status">
          {mode === "location"
            ? "The location map is unavailable right now."
            : "The map is unavailable right now. Use the list to browse facilities."}
        </Alert>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: mode === "location" ? 240 : { xs: 320, sm: 400 },
        height: fill
          ? // Phone: app bar, search band and bottom nav; desktop: app bar and search band.
            { xs: "calc(100dvh - 18rem - env(safe-area-inset-bottom, 0px))", md: "calc(100dvh - 15rem)" }
          : undefined,
        overflow: "hidden",
        border: fill ? { xs: 0, sm: 1 } : 1,
        borderColor: "divider",
        borderRadius: fill ? { xs: 0, sm: 3 } : 2,
        mx: fill ? { xs: -1.5, sm: 0 } : undefined,
        bgcolor: "background.paper",
      }}
      aria-busy={controller.loadState === "loading"}
    >
      {controller.loadState === "loading" ? (
        <LinearProgress aria-label="Loading facility map" />
      ) : null}

      <Box
        ref={controller.containerRef}
        role="application"
        aria-label="Facility map"
        sx={{ position: "absolute", inset: 0 }}
      />

      {overlay ? (
        <Box sx={{ position: "absolute", top: 12, insetInline: 12, pointerEvents: "none", "& > *": { pointerEvents: "auto" } }}>
          {overlay}
        </Box>
      ) : null}

      {discovery && allowAreaSearch ? (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: selectedFacility ? 96 : 16,
          }}
        >
          <Button
            size="small"
            variant="contained"
            color="inherit"
            startIcon={<SearchRounded />}
            onClick={controller.handleSearchArea}
            disabled={controller.loadState !== "ready"}
            sx={{ borderRadius: 999, bgcolor: "background.paper", color: "text.primary", boxShadow: 4, whiteSpace: "nowrap" }}
          >
            Search this area
          </Button>
        </Box>
      ) : null}

      {viewer.supported ? (
        <IconButton
          aria-label="Show my location"
          title={
            viewer.status === "denied"
              ? "Location is off for this site"
              : viewer.status === "unavailable"
                ? "Couldn't find your location. Try again."
                : "Show my location"
          }
          disabled={controller.loadState !== "ready" || viewer.status === "prompting"}
          onClick={viewer.locate}
          sx={{
            position: "absolute",
            right: 12,
            // Clear of Google's controls: its zoom buttons on desktop, its
            // attribution on phones (where the map has no buttons of its own).
            bottom: selectedFacility ? 152 : isPhone ? 28 : 112,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            boxShadow: 2,
            color: viewer.location ? locationDot : "text.secondary",
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <MyLocationRounded fontSize="small" />
        </IconButton>
      ) : null}

      {selectedFacility ? (
        <Paper
          component="section"
          ref={detailsRef}
          tabIndex={-1}
          aria-label={`${selectedFacility.name} map details`}
          elevation={8}
          sx={{
            position: "absolute",
            insetInline: 12,
            bottom: 12,
            p: 1.25,
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Avatar variant="rounded" aria-hidden sx={{ width: 44, height: 44, fontSize: 16, fontWeight: 700, borderRadius: 2 }}>
              {initials(selectedFacility.name)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontWeight: 700 }}>
                {selectedFacility.name}
              </Typography>
              {selectedFacility.subtitle ? (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {selectedFacility.subtitle}
                </Typography>
              ) : null}
            </Box>
            {mode === "discovery" && onSelectFacility ? (
              <Button
                size="small"
                variant="contained"
                onClick={() => onSelectFacility(selectedFacility)}
                sx={{ borderRadius: 999, whiteSpace: "nowrap" }}
              >
                View facility
              </Button>
            ) : null}
            <IconButton
              size="small"
              aria-label="Close facility details"
              onClick={() => setSelectedFacility(null)}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>
      ) : null}
    </Box>
  );
}
